import { Dialog, DialogContent, Icon } from '@material-ui/core'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import UploadImage from 'app/componentV4/Input/UploadImage'
import { CLOSE, SECONDARY } from 'app/constants/button'
import clsx from 'clsx'
import { usePlace } from 'commons/usePlace'
import { Field, Form, Formik } from 'formik'
import { curry, drop, lensPath, startsWith, view, when } from 'ramda'
import { FormattedMessage, useIntl } from 'react-intl'
import * as Yup from 'yup'
import { useMutation } from '@tanstack/react-query'
import effects from 'app/effects/effects'
import { fileToBase64 } from 'libs/file'
import { isEmptyOrNil } from 'libs/empty'
import MessageAlert from 'app/components/MessageAlert'
import { messageAlertError } from 'commons/error'
import { useDispatch } from 'react-redux'
import { useState } from 'react'
import apiConfigs from 'app/configs/apiConfigs'

const validationSchema = Yup.object().shape({
  profileUrl: Yup.mixed().required('Logo is required'),
  profileName: Yup.string()
    .test({
      name: 'required',
      exclusive: false,
      message: <FormattedMessage id="composeDialog.field.required" />,
      test: function (value) {
        return !isEmptyOrNil(value)
      },
    })

    .test({
      name: 'max',
      exclusive: false,
      message: (
        <FormattedMessage
          id="createNotification.dialog.validate.maxLength.message"
          values={{ maxLength: 20 }}
        />
      ),
      test: function (value) {
        if (isEmptyOrNil(value)) {
          return true
        }
        return value.length <= 20
      },
    }),
})

const removeBase = curry((base, val) =>
  when(
    (x) => typeof x === 'string' && startsWith(base, x),
    (x) => drop(base.length, x),
  )(val),
)

export default ({ open, onClose, onSuccess }) => {
  const place = usePlace()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(false)

  const lineRequestApi = async ({ profileUrl, profileName }) => {
    setLoading(true)
    await effects.apis.integration
      .line({
        profileUrl: removeBase(`${apiConfigs.cndUrl}/`, profileUrl),
        profileName,
      })
      .then(() => {
        setLoading(false)
        onSuccess()
        onClose()
      })
      .catch((error) => {
        const msg = error.response?.data
        dispatch(
          MessageAlert.showError({
            message: messageAlertError(msg, formatMessage),
          }),
        )
      })
  }

  const uploadAvatar = useMutation({
    mutationFn: async ({ base64 }) => {
      const response = await effects.apis.uploadLineProfile({
        data: { file: base64 },
      })
      return response
    },
    onSuccess: (response, { setFieldValue, setFieldTouched }) => {
      setFieldValue('profileUrl', response.cdnUrl)
      setFieldTouched('profileUrl', true, false)
    },
    onError: (error, { setFieldError, setFieldTouched }) => {
      const msg = error.response?.data
      setFieldTouched('profileUrl', true, false)
      setFieldError('profileUrl', messageAlertError(msg, formatMessage))
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(msg, formatMessage),
        }),
      )
    },
  })

  const handleUploadLineLogo = async (
    file,
    setFieldTouched,
    setFieldValue,
    setFieldError,
  ) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
    const maxSize = 3 * 1024 * 1024 // 3 MB

    if (!allowedTypes.includes(file.type)) {
      setFieldError('profileUrl', 'Only JPG, JPEG, and PNG files are allowed')
      setFieldTouched('profileUrl', true, false)
      return
    }

    if (file.size > maxSize) {
      setFieldError('profileUrl', 'File size must be 3 MB or smaller')
      setFieldTouched('profileUrl', true, false)
      return
    }

    const base64 = await fileToBase64(file)

    uploadAvatar.mutate({
      base64,
      setFieldValue,
      setFieldTouched,
    })
  }

  return (
    <Dialog open={open}>
      <Formik
        initialValues={{
          profileUrl: view(lensPath(['info', 'logo']), place),
          profileName: '',
        }}
        enableReinitialize={true}
        validateOnChange={true}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          lineRequestApi(values)
        }}
      >
        {({
          values,
          dirty,
          isValid,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          resetForm,
          setFieldError,
        }) => {
          return (
            <>
              <div className="flex justify-end p-4">
                <Icon
                  color="action"
                  className="m-4"
                  onClick={() => {
                    onClose()
                    resetForm()
                  }}
                >
                  close
                </Icon>
              </div>

              <DialogContent className="space-y-10 mb-5">
                <div>
                  <div className="text-16 font-medium text-blue-950 mb-2">
                    <FormattedMessage id="integrations.line.header.dialog" />
                  </div>
                  <div className="text-14 text-blue-950 mb-5">
                    <FormattedMessage id="integrations.line.body1.dialog" />
                  </div>
                  <div className="text-14 text-blue-950">
                    <span className="font-bold">
                      <FormattedMessage id="text.important" />
                    </span>
                    {` : ${formatMessage({ id: 'integrations.line.body2.dialog' })}`}
                  </div>
                </div>
                <Form className="space-y-5">
                  <div>
                    <div className="text-14 font-semibold text-blue-950 mb-4">
                      <FormattedMessage id="integrations.line.upload.logo.label" />
                    </div>
                    <div className="flex">
                      <Field name="profileUrl">
                        {() => (
                          <div
                            className={clsx(
                              'z-99 rounded-8 h-[8.2rem]',
                              errors.profileUrl &&
                                touched.profileUrl &&
                                'border border-red ',
                            )}
                          >
                            <UploadImage
                              src={values.profileUrl}
                              id="profileUrl-line"
                              name="profileUrl"
                              noLimit={true}
                              onChange={(file) =>
                                handleUploadLineLogo(
                                  file,
                                  setFieldTouched,
                                  setFieldValue,
                                  setFieldError,
                                )
                              }
                              title={
                                <div className="text-blue-950">
                                  <FormattedMessage id="integrations.line.upload.logo.label" />
                                </div>
                              }
                              isLoading={uploadAvatar.isPending}
                            />
                          </div>
                        )}
                      </Field>
                      <div className="px-5 text-14 text-primary-300 my-auto leading-[1.8]">
                        <div>File formats: JPG, JPEG, PNG</div>
                        <div>File size: 3 MB or smaller</div>
                        <div>
                          Recommended image dimensions: 640 × 640 pixels
                        </div>
                      </div>
                    </div>
                    {errors.profileUrl && touched.profileUrl && (
                      <div className="text-red">{errors.profileUrl}</div>
                    )}
                  </div>
                  <div className="text-14 font-semibold text-blue-950">
                    <label>Line Official Name *</label>
                    <Field name="profileName">
                      {({ field }) => (
                        <SimpleInput
                          id="line-placeholder"
                          name="profileName"
                          placeholder={formatMessage({
                            id: 'name.line.pleceholder',
                          })}
                          value={values.profileName}
                          onChange={(event) => {
                            setFieldValue(field.name, event.target.value)
                            setFieldTouched(field.name, true, false)
                          }}
                          fullWidth
                          error={
                            touched.profileName && Boolean(errors.profileName)
                          }
                          helperText={touched.profileName && errors.profileName}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="w-full flex justify-center space-x-5">
                    <OutlinedButtons
                      id="close-line-info-btn"
                      className="rounded-8"
                      color={CLOSE}
                      disabled={uploadAvatar.isPending || isLoading}
                      onClick={() => {
                        onClose()
                        resetForm()
                      }}
                    >
                      <FormattedMessage id="action.cancel" />
                    </OutlinedButtons>
                    <ContainedButton
                      id="save-line-info-btn"
                      className="rounded-8"
                      disabled={
                        !dirty ||
                        !isValid ||
                        uploadAvatar.isPending ||
                        isLoading
                      }
                      type="submit"
                      color={SECONDARY}
                    >
                      <FormattedMessage id="text.request" />
                    </ContainedButton>
                  </div>
                </Form>
              </DialogContent>
            </>
          )
        }}
      </Formik>
    </Dialog>
  )
}
